import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { mdiChevronLeft } from '@mdi/js';
import Icon from '@mdi/react';
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Radio,
	RadioGroup,
} from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGetPaymentMethods, useMonitoringStore } from 'services/monitoring';
import { placeOrder } from 'services/monitoring/monitoringService';
import * as Yup from 'yup';
import { InvoiceStep } from './InvoiceStep';

const invoiceType = 'pay_with_invoice';

const validationSchema = Yup.object({
	paymentType: Yup.string().required('Please select a payment method'),
});

type RadioOption = {
	id: string;
	payment_type: string;
	slug: string;
	payment_image: string;
};

const LoadingIndicator = () => (
	<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
		<CircularProgress />
	</Box>
);

const RadioButtonGroup = ({
	options,
	name,
	value,
	onChange,
	error,
}: {
	options: RadioOption[];
	name: string;
	value: string;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	error?: string;
}) => (
	<FormControl error={!!error}>
		<RadioGroup aria-labelledby={`${name}-label`} name={name} value={value} onChange={onChange}>
			{options.map(({ id, payment_image, payment_type, slug }) => (
				<div key={id} className="flex items-center">
					<FormControlLabel
						value={slug}
						control={<Radio />}
						label={<span className="text-secondary text-base font-roboto">{payment_type}</span>}
					/>
					{payment_image && (
						<img
							src={payment_image}
							alt={payment_type}
							className={`w-full h-full ${id === 'payInPartiallyBog' ? 'max-w-32' : 'max-w-9'}`}
						/>
					)}
				</div>
			))}
		</RadioGroup>
		{error && <FormHelperText>{error}</FormHelperText>}
	</FormControl>
);

export const PaymentStep = () => {
	const [showInvoiceForm, setShowInvoiceForm] = useState(false);
	const { t, i18n } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { setStepsDraft, stepsDraft, initialStepData } = useMonitoringStore();
	const { data: paymentMethods, isLoading } = useGetPaymentMethods();
	const [submitIsLoading, setSubmitIsLoading] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const location = useLocation();

	// Check if we're in the monitoring-v2 flow
	const isMonitoringV2 = location.pathname.includes('monitoring-v2');

	const formik = useFormik({
		initialValues: stepsDraft.payment || { paymentType: '' },
		validationSchema,
		onSubmit: async (values) => {
			setStepsDraft({ payment: values });
			setSubmitIsLoading(true);
			if (values.paymentType === invoiceType) {
				setShowInvoiceForm(true);
			} else {
				try {
					const response = await placeOrder(
						initialStepData.id,
						paymentMethods.find((method: any) => method.slug === values.paymentType)?.id
					);

					const { redirect_url } = (response as { data: { redirect_url: string } }).data;
					localStorage.setItem('languageBeforePayment', i18n.language);

					// Clear form data and IDs only when payment is actually being processed
					localStorage.removeItem('monitoringPackagesFormData');
					localStorage.removeItem('initialStepDataId');
					console.log('Payment processing, cleared all form data');

					window.location.href = redirect_url;
					formik.resetForm();
					setSubmitIsLoading(false);
				} catch (err) {
					setSubmitIsLoading(false);
				}
			}
		},
	});

	useEffect(() => {
		if (!initialStepData.id) {
			// Check if we're in the monitoring-v2 flow and adjust the navigation path accordingly
			const packagesPath = isMonitoringV2
				? '/farmer/services/monitoring-v2/packages'
				: '/farmer/services/monitoring/packages';
			navigateWithLanguage(packagesPath);
		}
	}, [initialStepData.id, navigateWithLanguage, isMonitoringV2]);

	const handleNavigateBack = useCallback(() => {
		if (showInvoiceForm) {
			setShowInvoiceForm(false);
			setSubmitIsLoading(false);
		} else {
			// Check if we're in the monitoring-v2 flow and adjust the navigation path accordingly
			const termsPath = isMonitoringV2
				? '/farmer/services/monitoring-v2/terms'
				: '/farmer/services/monitoring/terms';
				
			// Navigate back to terms page
			navigateWithLanguage(termsPath);
		}
	}, [showInvoiceForm, navigateWithLanguage, isMonitoringV2]);

	return (
		<form
			className="flex flex-col h-full gap-5 p-5 relative justify-between"
			onSubmit={formik.handleSubmit}
		>
			{!showInvoiceForm ? (
				<div className="flex items-center justify-center flex-1 mt-5 sm:mt-0">
					<div className="flex flex-col max-w-[500px] w-full gap-5">
						{isLoading ? (
							<LoadingIndicator />
						) : (
							<>
								<h1 className="text-dark text-center font-bold text-xl leading-normal mb-5 mt-10">
									{t('findSpecialistPage.monitoring.paymentMethods')}
								</h1>
								<RadioButtonGroup
									options={paymentMethods || []}
									name="paymentType"
									value={formik.values.paymentType}
									onChange={(e) => formik.setFieldValue('paymentType', e.target.value)}
									error={
										formik.touched.paymentType && typeof formik.errors.paymentType === 'string'
											? formik.errors.paymentType
											: undefined
									}
								/>

								<Button
									className="w-full"
									type="submit"
									loading={isLoading || submitIsLoading}
									variant="contained"
									disabled={!formik.values.paymentType}
								>
									{t('findSpecialistPage.monitoring.placeOrder')}
								</Button>
							</>
						)}
					</div>
				</div>
			) : (
				<InvoiceStep setSuccess={(e: any) => setIsSuccess(e)} />
			)}

			{!isSuccess && (
				<Button
					className="w-[120px]"
					type="button"
					onClick={handleNavigateBack}
					sx={{ backgroundColor: 'white', color: 'black' }}
					variant="contained"
					startIcon={<Icon path={mdiChevronLeft} size={1} />}
				>
					{t('previous')}
				</Button>
			)}
		</form>
	);
};
